import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import type { LLCheckGameStateElement } from '../../types';

/**
 * Renders a game-state requirement and shows live ✓/✗ based on whether the
 * player has a `dfe_game_states` row at keyword `${gameKey}:${gameState}`.
 *
 * Contract: the originating game writes the row when the event fires.
 * Lit-legion is a read-only consumer; the server re-checks on submit.
 */
export function CheckGameStateElement({ element }: { element: LLCheckGameStateElement }) {
  const keyword = `${element.gameKey}:${element.gameState}`;

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.gameState.detail(keyword),
    queryFn: async () => {
      try {
        return await api.getGameState(keyword);
      } catch (err) {
        // 404 means the event hasn't been recorded yet — treat as "not satisfied".
        if (err instanceof Error && /404|not.?found/i.test(err.message)) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const satisfied = !isLoading && !isError && data !== null && data !== undefined;

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        isLoading && 'opacity-70',
        !isLoading && satisfied && 'border-green-500/40 bg-green-500/5',
        !isLoading && !satisfied && 'border-amber-500/40 bg-amber-500/5',
      )}
    >
      <span aria-hidden className="mt-0.5">
        {isLoading ? (
          <span className="inline-block h-4 w-4 animate-pulse rounded-full bg-muted-foreground/30" />
        ) : satisfied ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <X className="h-4 w-4 text-amber-600" />
        )}
      </span>
      <div className="flex-1">
        <p className="font-medium">{element.description}</p>
        <p className="text-xs text-muted-foreground font-mono">{keyword}</p>
      </div>
    </div>
  );
}
